import React, { useCallback, useEffect, useState } from 'react';
import { Card, Badge, Button, Form, Modal, Table, Row, Col } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import API from '../services/api';
import { dateTime } from './adminShared';
import Loading from './Loading';
import { reportName } from '../utils/fileName';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const rule = '1px solid var(--mm-line)';

// One automatic alert: what it is, whether it's on, and its own controls.
const AlertRow = ({ title, description, enabled, paused, busy, onToggle, children }) => (
  <div className="py-3" style={{ borderTop: rule }}>
    <div className="d-flex justify-content-between align-items-start">
      <div className="me-3" style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>
          {title}{' '}
          <Badge bg={enabled && !paused ? 'success' : 'secondary'}>{!enabled ? 'Desactivada' : paused ? 'En pausa' : 'Activada'}</Badge>
        </div>
        <div className="small text-muted">{description}</div>
      </div>
      <Button className="flex-shrink-0" size="sm" variant={enabled ? 'outline-secondary' : 'primary'} disabled={busy} onClick={onToggle}>
        {enabled ? 'Desactivar' : 'Activar'}
      </Button>
    </div>
    {children && <div className="mt-2">{children}</div>}
  </div>
);

const AdminNotificationsTab = () => {
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [preview, setPreview] = useState(null); // { url } | { error } | { loading: true }
  const { user: me } = useAuth();
  const [mine, setMine] = useState(null); // my own address + preferences
  const [myEmail, setMyEmail] = useState('');

  const load = useCallback(() => Promise.all([API.get('/admin/settings'), API.get('/account/notifications')])
    .then(([s, m]) => { setSettings(s.data); setMine(m.data); setMyEmail(m.data.email); })
    .catch(err => setError(err.response?.data?.message || 'No se pudo cargar la configuración.')), []);
  useEffect(() => { load(); }, [load]);

  const run = async (key, fn) => {
    setBusy(key); setError(''); setNotice('');
    try { const { data } = await fn(); setNotice(data.message); await load(); }
    catch (err) { setError(err.response?.data?.message || 'La acción falló.'); }
    finally { setBusy(''); }
  };
  const save = (key, patch) => run(key, () => API.put('/admin/settings', patch));
  const saveMine = (key, patch) => run(key, () => API.put('/account/notifications', patch));

  const candidate = newEmail.trim().toLowerCase();
  const duplicate = settings?.extraRecipients.includes(candidate);
  const addEmail = async () => {
    if (!EMAIL_RE.test(candidate) || duplicate) return;
    await save('recipients', { alertRecipients: [...settings.extraRecipients, candidate] });
    setNewEmail('');
  };

  const hourLabel = (h) => new Date(2000, 0, 1, h).toLocaleTimeString('es-DO', { hour: 'numeric', minute: '2-digit' });
  const dateLabel = (d) => (d ? d.split('-').reverse().join('/') : '');

  const openPreview = async () => {
    setPreview({ loading: true });
    try {
      const { data } = await API.get('/admin/daily-close/preview', { responseType: 'blob' });
      setPreview({ url: URL.createObjectURL(data) });
    } catch (err) {
      setPreview({ error: 'No se pudo generar el reporte.' });
    }
  };
  const closePreview = () => { if (preview?.url) URL.revokeObjectURL(preview.url); setPreview(null); };

  if (error && !settings) return <div className="alert alert-danger">{error}</div>;
  if (!settings || !mine) return <Loading label="Cargando configuración..." />;

  const on = settings.emailNotificationsEnabled;
  const closeRecipients = settings.recipientsByAlert.dailyClose;
  const cannotSend = !settings.mailConfigured || closeRecipients.length === 0;
  const alertNames = {
    staleInvoice: 'Facturas sin enviar a DGII', whatsappHandoff: 'WhatsApp: piden una persona', rentalDue: 'Alquileres', clientDeal: 'Acuerdos con clientes',
    dailyClose: 'Cierre del día', approvalRequest: 'Solicitudes de aprobación',
  };
  // Mine: the server's list, named like the table below.
  const myAlertName = (alert) => alertNames[alert] || (mine.alerts[alert].charAt(0).toUpperCase() + mine.alerts[alert].slice(1));
  const quiet = mine.quietHours;
  const setQuiet = (patch) => saveMine('quiet', { quietHours: patch });
  const receives = (list, alert) => {
    const byWhatsApp = settings.whatsappByAlert?.[alert] || [];
    return (
      <>
        {list.length ? <>Lo reciben por correo: <strong>{list.join(', ')}</strong></> : <span className="text-warning">Nadie lo recibe por correo ahora mismo (ningún administrador con correo lo tiene activado).</span>}
        {byWhatsApp.length > 0 && <div>Por WhatsApp: <strong>{byWhatsApp.join(', ')}</strong></div>}
      </>
    );
  };
  const whatsappHint = !settings.whatsappConfigured
    ? 'WhatsApp no está configurado en este servidor.'
    : !mine.whatsappPhone ? 'Para recibirlas por WhatsApp, agrega tu número en Usuarios → "Correo / WhatsApp".' : null;
  return (
    <>
      {error && <div className="alert alert-danger">{error}</div>}
      {notice && <div className="alert alert-success">{notice}</div>}

      <Row>
      <Col xl={6} className="mb-3">
      <Card className="shadow-sm p-3 h-100">
        <Card.Title style={{ fontSize: '1.15rem' }}>Notificaciones automáticas</Card.Title>
        <div className="small text-muted mb-3">
          Alertas automáticas del sistema, por correo y (para quien lo active) por WhatsApp. Los correos que envías tú a mano (como la contraseña temporal de un usuario) no dependen de estos interruptores.
        </div>

        <div className="d-flex justify-content-between align-items-center py-3" style={{ borderTop: rule }}>
          <div className="me-3" style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>Alertas automáticas{' '}
              <Badge bg={on ? 'success' : 'secondary'}>{on ? 'Activados' : 'Desactivados'}</Badge>
            </div>
            <div className="small text-muted">Interruptor general: mientras esté desactivado no se envía ninguna de las alertas de abajo, ni por correo ni por WhatsApp, aunque tengan su propio interruptor activo.</div>
            <div className="small text-muted">{settings.defaultsToOn ? 'En este servidor vienen activados por defecto.' : 'En modo de pruebas (sandbox) vienen apagados por defecto, para que nunca lleguen correos de prueba a tu equipo; en el servidor real vienen activados.'}</div>
            {settings.updatedBy && <div className="small text-muted mt-1">Último cambio: {settings.updatedBy}, {dateTime(settings.updatedAt)}</div>}
          </div>
          <Button className="flex-shrink-0" variant={on ? 'outline-secondary' : 'primary'} disabled={busy === 'master' || !settings.mailConfigured}
            onClick={() => save('master', { emailNotificationsEnabled: !on })}>
            {busy === 'master' ? 'Guardando...' : on ? 'Desactivar' : 'Activar'}
          </Button>
        </div>

        <div className="small text-muted text-uppercase mt-2 mb-1" style={{ letterSpacing: '0.06em' }}>Alertas</div>

        <AlertRow
          title="Facturas sin enviar a DGII"
          description="Aviso cuando una factura lleva más de 24 horas guardada sin enviarse a DGII (DGII exige hacerlo dentro de 72). Es un solo correo con todas las pendientes; se revisa cada 30 minutos."
          enabled={settings.staleInvoiceAlertEnabled} paused={!on} busy={!!busy}
          onToggle={() => save('stale', { staleInvoiceAlertEnabled: !settings.staleInvoiceAlertEnabled })}
        >
          <div className="small text-muted">{receives(settings.recipientsByAlert.staleInvoice, 'staleInvoice')}</div>
        </AlertRow>

        <AlertRow
          title="WhatsApp: piden hablar con una persona"
          description="Aviso cuando un cliente le pide al asistente de WhatsApp hablar con alguien (o el asistente no puede ayudarle), para responderle desde la bandeja de WhatsApp. WhatsApp permite responder hasta 24 horas después de su último mensaje."
          enabled={settings.whatsappHandoffAlertEnabled} paused={!on} busy={!!busy}
          onToggle={() => save('handoff', { whatsappHandoffAlertEnabled: !settings.whatsappHandoffAlertEnabled })}
        >
          <div className="small text-muted">{receives(settings.recipientsByAlert.whatsappHandoff, 'whatsappHandoff')}</div>
        </AlertRow>

        <AlertRow
          title="Cierre del día"
          description={<>Cada día, después de la hora elegida, se envía un <strong>PDF</strong> con toda la actividad financiera del día: ventas, cobros, gastos, notas de crédito y débito, cuentas por cobrar y el estado con DGII.</>}
          enabled={settings.dailyCloseEnabled} paused={!on} busy={!!busy}
          onToggle={() => save('close', { dailyCloseEnabled: !settings.dailyCloseEnabled })}
        >
          <div className="d-flex flex-wrap align-items-end gap-3">
            <div>
              <Form.Label className="d-block small text-muted mb-1">Hora de envío (República Dominicana)</Form.Label>
              <Form.Select size="sm" style={{ width: 150 }} value={settings.dailyCloseHour} disabled={!!busy}
                onChange={e => save('hour', { dailyCloseHour: Number(e.target.value) })}>
                {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
              </Form.Select>
            </div>
            <Button size="sm" variant="outline-primary" onClick={openPreview}><i className="bi bi-file-earmark-pdf me-1" />Ver el reporte de hoy</Button>
            <Button size="sm" variant="outline-primary" disabled={busy === 'send' || cannotSend}
              onClick={() => { if (window.confirm(`Se enviará ahora el cierre de hoy (con lo que lleva el día) a ${closeRecipients.join(', ')}. ¿Continuar?`)) run('send', () => API.post('/admin/daily-close/send', {})); }}>
              <i className="bi bi-envelope me-1" />{busy === 'send' ? 'Enviando...' : 'Enviar ahora'}
            </Button>
          </div>
          <div className="small text-muted mt-2">{receives(closeRecipients, 'dailyClose')}</div>
          <div className="small text-muted">
            {settings.lastDailyCloseDate ? <>Último envío: <strong>{dateLabel(settings.lastDailyCloseDate)}</strong></> : 'Aún no se ha enviado ninguno.'}
          </div>
        </AlertRow>

        <AlertRow
          title="Acuerdos con clientes"
          description="Aviso cuando un acuerdo (Conduces → Acuerdos) llega al 80 % usado, cuando se agota — desde entonces los conduces de ese cliente se facturan — y 15 días antes de su fecha de fin. Un aviso por evento; se revisa cada 30 minutos."
          enabled={settings.clientDealAlertEnabled} paused={!on} busy={!!busy}
          onToggle={() => save('deals', { clientDealAlertEnabled: !settings.clientDealAlertEnabled })}
        >
          <div className="small text-muted">{receives(settings.recipientsByAlert.clientDeal || [], 'clientDeal')}</div>
        </AlertRow>

        <AlertRow
          title="Alquileres"
          description="Cada mañana, desde la hora elegida, lo que necesitan los alquileres ese día: los equipos que regresan hoy o mañana, los que pasaron su fecha de fin sin devolverse y las facturas de alquiler que toca hacer (o ya tocaban). Un solo aviso al día, y solo si hay algo pendiente."
          enabled={settings.rentalAlertEnabled} paused={!on} busy={!!busy}
          onToggle={() => save('rental', { rentalAlertEnabled: !settings.rentalAlertEnabled })}
        >
          <div>
            <Form.Label className="d-block small text-muted mb-1">Hora del aviso (República Dominicana)</Form.Label>
            <Form.Select size="sm" style={{ width: 150 }} value={settings.rentalAlertHour} disabled={!!busy}
              onChange={e => save('rentalHour', { rentalAlertHour: Number(e.target.value) })}>
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
            </Form.Select>
          </div>
          <div className="small text-muted mt-2">{receives(settings.recipientsByAlert.rentalDue || [], 'rentalDue')}</div>
          <div className="small text-muted">
            {settings.lastRentalAlertDate ? <>Último día revisado: <strong>{dateLabel(settings.lastRentalAlertDate)}</strong></> : 'Aún no se ha revisado ningún día.'}
          </div>
        </AlertRow>

      </Card>
      </Col>

      <Col xl={6} className="mb-3">
      <Card className="shadow-sm p-3 h-100">
        <Card.Title style={{ fontSize: '1.15rem' }}>Personas y destinatarios</Card.Title>
        <div className="small text-muted mb-3">Quién recibe las alertas de la izquierda y cómo. Cada persona ajusta solo las suyas.</div>

        <div className="py-3" style={{ borderTop: rule }}>
          <div style={{ fontWeight: 600 }}>Mis notificaciones <span className="small text-muted" style={{ fontWeight: 400 }}>— cada persona elige las suyas; el correo viene activado y WhatsApp apagado hasta que lo actives</span></div>
          <Form onSubmit={e => { e.preventDefault(); saveMine('myemail', { email: myEmail }); }} className="d-flex flex-wrap gap-2 align-items-end my-2">
            <div style={{ flex: '1 1 260px', maxWidth: 360 }}>
              <Form.Label className="d-block small text-muted mb-1">Mi correo</Form.Label>
              <Form.Control size="sm" type="email" placeholder="tu@correo.com" value={myEmail} onChange={e => setMyEmail(e.target.value)} />
            </div>
            <Button size="sm" type="submit" variant="outline-primary" disabled={!!busy || myEmail.trim().toLowerCase() === mine.email || (myEmail.trim() !== '' && !EMAIL_RE.test(myEmail.trim()))}>Guardar</Button>
          </Form>
          {!mine.email && <div className="small text-warning mb-2">Sin correo no recibes ninguna alerta. Agrégalo arriba.</div>}
          <Table size="sm" className="mb-1 align-middle" style={{ maxWidth: 620 }}>
            <thead><tr><th>Alerta</th><th className="text-center">Correo</th><th className="text-center">WhatsApp</th><th className="text-center">Notificación push</th></tr></thead>
            <tbody>
              {Object.keys(mine.alerts).map(alert => (
                <tr key={alert}>
                  <td>{myAlertName(alert)}</td>
                  <td className="text-center">
                    <Button size="sm" variant={mine.prefs[alert].email ? 'success' : 'outline-secondary'} disabled={!!busy}
                      onClick={() => saveMine('pref', { prefs: { [alert]: { email: !mine.prefs[alert].email } } })}>
                      {mine.prefs[alert].email ? 'Sí' : 'No'}
                    </Button>
                  </td>
                  <td className="text-center">
                    {mine.whatsappAvailable ? (
                      <Button size="sm" variant={mine.prefs[alert].whatsapp ? 'success' : 'outline-secondary'} disabled={!!busy}
                        onClick={() => saveMine('pref', { prefs: { [alert]: { whatsapp: !mine.prefs[alert].whatsapp } } })}>
                        {mine.prefs[alert].whatsapp ? 'Sí' : 'No'}
                      </Button>
                    ) : <span className="text-muted">—</span>}
                  </td>
                  <td className="text-center"><Badge bg="secondary">Próximamente</Badge></td>
                </tr>
              ))}
            </tbody>
          </Table>
          {whatsappHint && <div className="small text-muted">{whatsappHint}</div>}
          {mine.whatsappAvailable && <div className="small text-muted">Por WhatsApp llegan a +{mine.whatsappPhone}. Si respondes, te contesta el asistente de Intmed con los números al día. También puedes pedirle que cambie estas opciones.</div>}
          <div className="d-flex flex-wrap align-items-end gap-2 mt-3">
            <div>
              <Form.Label className="d-block small text-muted mb-1">Horas de silencio (WhatsApp)</Form.Label>
              <Form.Select size="sm" style={{ width: 210 }} disabled={!!busy} value={quiet ? 'on' : 'off'}
                onChange={e => setQuiet(e.target.value === 'on' ? { start: 21, end: 7 } : null)}>
                <option value="off">Sin horas de silencio</option>
                <option value="on">Con horas de silencio</option>
              </Form.Select>
            </div>
            {quiet && (
              <>
                <div>
                  <Form.Label className="d-block small text-muted mb-1">Desde</Form.Label>
                  <Form.Select size="sm" style={{ width: 130 }} disabled={!!busy} value={quiet.start}
                    onChange={e => setQuiet({ ...quiet, start: Number(e.target.value) })}>
                    {Array.from({ length: 24 }, (_, h) => <option key={h} value={h} disabled={h === quiet.end}>{hourLabel(h)}</option>)}
                  </Form.Select>
                </div>
                <div>
                  <Form.Label className="d-block small text-muted mb-1">Hasta</Form.Label>
                  <Form.Select size="sm" style={{ width: 130 }} disabled={!!busy} value={quiet.end}
                    onChange={e => setQuiet({ ...quiet, end: Number(e.target.value) })}>
                    {Array.from({ length: 24 }, (_, h) => <option key={h} value={h} disabled={h === quiet.start}>{hourLabel(h)}</option>)}
                  </Form.Select>
                </div>
              </>
            )}
          </div>
          <div className="small text-muted mt-1">
            {quiet ? 'En ese horario los WhatsApp automáticos esperan y te llegan al terminar. El correo llega siempre al momento; los recordatorios que tú pides llegan a la hora que pediste.' : 'Los WhatsApp automáticos te llegan a cualquier hora.'}
          </div>
        </div>

        <div className="py-3" style={{ borderTop: rule }}>
          <div style={{ fontWeight: 600 }}>Quién recibe qué <span className="small text-muted" style={{ fontWeight: 400 }}>— todos los administradores con correo, salvo quien lo desactive; <i className="bi bi-whatsapp" /> = también por WhatsApp</span></div>
          <Table size="sm" className="my-2 align-middle">
            <thead><tr><th>Administrador</th><th>Correo</th>{Object.keys(alertNames).map(a => <th key={a} className="text-center">{alertNames[a]}</th>)}</tr></thead>
            <tbody>
              {settings.admins.map(a => (
                <tr key={a.username}>
                  <td>{a.username} {a.username === me?.username && <Badge bg="info">tú</Badge>}</td>
                  <td className="small">{a.email || <span className="text-warning">sin correo: no recibe nada</span>}</td>
                  {Object.keys(alertNames).map(alert => (
                    <td key={alert} className="text-center text-nowrap">
                      {!a.email ? <span className="text-muted">—</span> : a.optOut.includes(`email:${alert}`) ? <Badge bg="secondary">No</Badge> : <Badge bg="success">Sí</Badge>}
                      {settings.whatsappByAlert?.[alert]?.includes(a.username) && <Badge bg="success" className="ms-1" title="También por WhatsApp"><i className="bi bi-whatsapp" /></Badge>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
          <div className="small text-muted">Cada quien cambia solo sus propias preferencias, arriba. Falta el correo de alguien: agrégalo en la pestaña Usuarios.</div>
        </div>

        <div className="py-3" style={{ borderTop: rule }}>
          <div style={{ fontWeight: 600 }}>Otros destinatarios <span className="small text-muted" style={{ fontWeight: 400 }}>— personas sin cuenta; reciben todas las alertas</span></div>
          <div className="d-flex flex-wrap gap-2 my-2">
            {settings.extraRecipients.map(r => (
              <Badge key={r} bg="info" className="d-inline-flex align-items-center" style={{ fontWeight: 500, fontSize: '0.85rem', padding: '0.4em 0.6em' }}>
                {r}
                <button type="button" aria-label={`Quitar ${r}`} title="Quitar" disabled={!!busy} onClick={() => save('recipients', { alertRecipients: settings.extraRecipients.filter(x => x !== r) })}
                  style={{ background: 'none', border: 0, color: 'inherit', padding: 0, marginLeft: 8, lineHeight: 1, cursor: 'pointer', opacity: 0.8 }}>
                  <i className="bi bi-x-lg" />
                </button>
              </Badge>
            ))}
            {settings.extraRecipients.length === 0 && <span className="text-muted small">Ninguno.</span>}
          </div>
          <Form onSubmit={e => { e.preventDefault(); addEmail(); }} className="d-flex gap-2" style={{ maxWidth: 460 }}>
            <Form.Control size="sm" type="email" placeholder="correo@ejemplo.com" value={newEmail} onChange={e => setNewEmail(e.target.value)} />
            <Button size="sm" type="submit" variant="outline-primary" style={{ whiteSpace: 'nowrap' }} disabled={!!busy || !EMAIL_RE.test(candidate) || duplicate}>
              <i className="bi bi-plus-lg me-1" />Agregar
            </Button>
          </Form>
          {duplicate && <div className="small text-muted mt-1">Ese correo ya está en la lista.</div>}

          <div className="small text-muted mt-3 mb-1">Enviados desde</div>
          <div>{settings.sender || <span className="text-muted">Correo del servidor sin configurar (SMTP_* en el servidor).</span>}</div>
          <Button className="mt-3" size="sm" variant="outline-primary" disabled={busy === 'test' || !settings.mailConfigured || !mine.email}
            onClick={() => { if (window.confirm(`Se enviará un correo de prueba a ${mine.email}. ¿Continuar?`)) run('test', () => API.post('/admin/test-email')); }}>
            <i className="bi bi-envelope me-1" />{busy === 'test' ? 'Enviando...' : 'Enviarme un correo de prueba'}
          </Button>
        </div>
      </Card>
      </Col>
      </Row>

      <Modal show={!!preview} onHide={closePreview} size="xl" centered>
        <Modal.Header closeButton><Modal.Title>Cierre del día — vista previa</Modal.Title></Modal.Header>
        <Modal.Body style={{ height: '75vh', padding: 0 }}>
          {preview?.loading && <div className="text-muted p-4">Generando el reporte...</div>}
          {preview?.error && <div className="alert alert-danger m-3">{preview.error}</div>}
          {preview?.url && <iframe title="Cierre del día" src={preview.url} style={{ width: '100%', height: '100%', border: 0 }} />}
        </Modal.Body>
        <Modal.Footer>
          {preview?.url && <a className="btn btn-outline-primary btn-sm" href={preview.url} download={`${reportName('Cierre del Día')}.pdf`}><i className="bi bi-download me-1" />Descargar PDF</a>}
          <Button size="sm" onClick={closePreview}>Cerrar</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AdminNotificationsTab;
